import React from 'react';
import { FormattedMessage } from 'react-intl';

import { LegOptionContext } from 'Modules/MultiStopWizard/Components/Leg/Contexts/LegOptionContext';
import { isTripClosedByBookingType } from 'Services/Api/_HumanWritten/BookingWizardDataService';
import Icon from 'Views/Components/_HumanWritten/Icon/Icon';
import Popover from 'Views/Components/_HumanWritten/Popover';
import { hasDeparted } from './tripOptionState';

/**
 * What the row's status means, on demand.
 *
 * <p><b>It explains the status that is showing, not the first thing it finds true.</b> This started
 * as a closed-only mark and was drawn whenever the trip was closed — but a sailing that has already
 * left is very often closed as well, and `useLegErrorMessage` prints `Departed` ahead of `Closed`.
 * So a row reading `SAILED` offered an ⓘ explaining that online bookings were no longer available
 * and to ring the office, which is advice about a different problem on a ferry that has gone.</p>
 *
 * <p>Ordered the way the message it sits beside is ordered, so the two cannot disagree again.</p>
 */
export default function TripOptionStatusInfo() {
	const context = React.useContext(LegOptionContext);

	if (!context) {
		return null;
	}

	// Departed first, exactly as `useLegErrorMessage` decides it.
	const message = (() => {
		if (hasDeparted(context)) {
			return (
				<FormattedMessage
					id="trip-option__departed__info"
					defaultMessage="This trip has already departed and can no longer be booked."
				/>
			);
		}
		if (isTripClosedByBookingType(context.trip, context.wizardData.tabSelected)) {
			return (
				<FormattedMessage
					id="capacity-closed__info__message"
					defaultMessage="Online bookings are no longer available for this trip. Please contact staff to book."
				/>
			);
		}
		return null;
	})();

	// Sold out, over capacity, clashing with the other leg — the status says all it needs to.
	if (message === null) {
		return null;
	}

	return (
		<Popover
			floating
			openOnHover
			// The row behind this is itself a button — see `stopPropagation`.
			stopPropagation
			containerClassName="trip-status"
			buttonClassName="trip-status__trigger"
			popoverClassName="trip-status__panel"
			buttonTestId="trip-option-status-info"
			buttonContent={
				<span aria-label="Why" role="img">
					<Icon name="info" useMaterialIcons />
				</span>
			}
			popoverContent={() => message}
		/>
	);
}
